import React from "react"
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  PaperProps,
  Typography,
  TypographyProps
} from "@mui/material"
import { FaIcon } from "../../../../mp-core/icons"
import IconButton, { IconButtonProps } from "../IconButton"
import { ScreenReaderAnnouncement } from "@masteryprep/mp-shared-components/generic"
import { useStyles, CustomDialogStyles } from "./styles"

// Templates built on CustomDialog — prefer a matching template before composing
// this directly, so these dialogs stay consistent:
//   • IconPromptDialog — icon-topped prompt (icon + title + description +
//     primary/cancel actions), e.g. Get Ready / Time's Up.
interface Props extends Omit<DialogProps, "title"> {
  title?: React.ReactNode
  actions?: React.ReactNode
  footer?: React.ReactNode
  classes?: Partial<Record<CustomDialogStyles, string>>
  withCloseIcon?: boolean
  titleProps?: Partial<TypographyProps> & {
    "data-automation-id"?: string
    component?: React.ElementType
  }
  backdropProps?: Partial<React.HTMLAttributes<HTMLDivElement>> & {
    "data-automation-id"?: string
  }
  paperProps?: Partial<PaperProps> & { "data-automation-id"?: string }
  CloseButtonProps?: Partial<IconButtonProps> & {
    "data-automation-id"?: string
  }
  size?: "xs" | "sm" | "md" | "lg" | "xl"
  // a11y: announce this message from a live region INSIDE the dialog. The app-wide
  // announcer (useAnnounce) is aria-hidden by the dialog's focus trap, so loading /
  // status text originating in dialog content must be announced here instead.
  a11yAnnouncement?: string
}

let dialogIdCounter = 0

const CustomDialog: React.FC<React.PropsWithChildren<Props>> = props => {
  const {
    open,
    onClose,
    title,
    titleProps = {},
    backdropProps = {},
    paperProps = {},
    children,
    actions,
    footer,
    classes: outerClasses,
    withCloseIcon = true,
    CloseButtonProps,
    size = "md",
    fullScreen,
    a11yAnnouncement,
    ...rest
  } = props

  const dialogId = React.useMemo(() => `custom-dialog-${++dialogIdCounter}`, [])
  const titleId = `${dialogId}-title`
  const contentId = `${dialogId}-content`
  const documentRef = React.useRef<HTMLDivElement>(null)

  const { classes, cx } = useStyles({ size, withCloseIcon, fullScreen })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // a11y: suppress aria-labelledby — title is read naturally from the role="document"
      // region below. Without this, NVDA announces the dialog name (title) on open AND
      // again when browsing the document region, causing the title to be read 3×.
      aria-labelledby={undefined}
      TransitionProps={{
        onEntered: () => {
          documentRef.current?.focus()
        }
      }}
      PaperProps={{
        className: cx(classes.paper, outerClasses?.paper),
        // a11y: null removes MUI's auto-generated or caller-supplied aria-labelledby
        // from the Paper so NVDA doesn't announce the title a second time via the dialog label.
        "aria-labelledby": null,
        "aria-modal": true,
        ...paperProps
      }}
      componentsProps={{
        backdrop: {
          className: cx(classes.backdrop, outerClasses?.backdrop),
          ...backdropProps
        }
      }}
      fullScreen={fullScreen}
      // a11y: disableEnforceFocus stops MUI's FocusTrap from actively pulling
      // focus back, which interferes with ChromeVox's virtual cursor (Search+Arrow).
      disableEnforceFocus
      // a11y: disableAutoFocus prevents MUI from briefly focusing the first button
      // before onEntered fires. Without this, NVDA announces "dialog" twice — once
      // for the button focus and once when we redirect focus to the document region.
      disableAutoFocus
      {...rest}
    >
      {/* a11y: role="document" wraps all dialog content so NVDA enters Browse Mode and
          arrow keys work. It starts before DialogTitle so the title is read exactly once
          as the first element in the browseable region. */}
      <Box
        ref={documentRef}
        role="document"
        tabIndex={-1}
        sx={{
          outline: "none",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0
        }}
      >
        <DialogTitle
          component="div"
          className={cx(classes.title, outerClasses?.title)}
        >
          {withCloseIcon && (
            <IconButton
              {...CloseButtonProps}
              onClick={event => onClose(event, "backdropClick")}
              className={cx(classes.closeButton, outerClasses?.closeButton)}
              data-automation-id="closeBtn"
              aria-label="Close dialog"
            >
              <FaIcon
                aria-hidden="true"
                solid
                name="times"
                size="med"
                className={cx(classes.closeIcon, outerClasses?.closeIcon)}
              />
            </IconButton>
          )}
          {title && (
            <Typography id={titleId} variant="h3" {...titleProps}>
              {title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent
          id={contentId}
          className={cx(classes.content, outerClasses?.content)}
        >
          {children}
        </DialogContent>
        {actions && (
          <DialogActions className={cx(classes.actions, outerClasses?.actions)}>
            {actions}
          </DialogActions>
        )}
      </Box>
      {footer}
      {a11yAnnouncement !== undefined && (
        <ScreenReaderAnnouncement assertive message={a11yAnnouncement} />
      )}
    </Dialog>
  )
}

export default CustomDialog
